import React, { useState } from 'react';
import { Modal, Button, Tag, Switch } from 'antd';
import { CheckCircleFilled, RocketOutlined, CrownOutlined, FireFilled } from '@ant-design/icons';
import './OnboardingPricingModal.css';
import createPaymentLink from '../createCheckout/main';
import confetti from 'canvas-confetti';

const OnboardingPricingModal = ({ open, onClose, messageApi, setPaymentLinkLoading, setPaymentLinkData }) => {

    const [billingCycle, setBillingCycle] = useState('yearly'); // 'monthly' or 'yearly'
    const [showFreeTrialSuccess, setShowFreeTrialSuccess] = useState(false);

    // Detect currency based on timezone
    const user_tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const showINR = user_tz === "Asia/Kolkata" || user_tz === "Asia/Calcutta";

    const plans = [
        {
            id: 'legend_pro',
            name: 'Legend Pro',
            icon: <CrownOutlined style={{ color: '#8b5cf6' }} />,
            monthly: { price: showINR ? '₹999' : '$12', id: '000003' },
            yearly: { price: showINR ? '₹833' : '$9.99', id: '0000031' },
            features: [
                'Unlimited Comment + Stories Automations',
                '800 replies/hour',
                'Facebook + Instagram',
            ],
            buttonText: 'Upgrade',
            buttonType: 'default',
            popular: false
        },
        {
            id: 'trendsetter',
            name: 'Trendsetter',
            icon: <FireFilled style={{ color: '#ef4444' }} />,
            monthly: { price: showINR ? '₹1,999' : '$29.99', id: '428337' }, // Adjusted based on context
            yearly: { price: showINR ? '₹1,666' : '$24.99', id: '428338' },
            features: [
                'Everything in Legend Pro',
                '2,000 replies/hour',
                'Flow Editor',
                'Shopify Integration',
                'Content Scheduler With Comment Automation (For 3 Months)',
                'Product Templates',
                'Viral Mode',
                'Advanced Dashboard'
            ],
            buttonText: 'Go Pro',
            buttonType: 'primary',
            popular: true,
            recommended: true
        },
        {
            id: 'trendsetter_pro',
            name: 'Trendsetter Pro',
            icon: <RocketOutlined style={{ color: '#10b981' }} />,
            monthly: { price: showINR ? '₹4,999' : '$59.99', id: 'trendsetter-pro-monthly' },
            yearly: { price: showINR ? '₹4,165' : '$49.99', id: 'trendsetter-pro-yearly' },
            features: [
                'Everything in Trendsetter',
                'Truly Unlimited',
                'Advanced Safety',
                'Priority Support',
                'Whatsapp Support',
                'Overload Handler'
            ],
            buttonText: 'Get Max',
            buttonType: 'default',
            popular: false
        }
    ];

    const freePlan = {
        id: 'free',
        name: 'Free Plan',
        icon: '🎁',
        price: showINR ? '₹0' : '$0',
        period: 'forever',
        features: [
            '1,500 Automations/month',
            'Link Unlimited Posts',
            'Comment to DM',
            'Basic Support'
        ],
        buttonText: 'Start for Free',
        buttonType: 'dashed'
    };

    const handleSelectPlan = (plan) => {
        if (plan.id === 'free') {
            setShowFreeTrialSuccess(true);
            confetti({
                particleCount: 200,
                spread: 100,
                origin: { y: 0.6 },
                zIndex: 10001 // Higher than modal
            });
        } else {
            // Call API to create payment link using monthly/yearly id
            const currency = showINR ? 'inr' : 'usd';
            const targetPlanId = billingCycle === 'yearly' ? plan.yearly.id : plan.monthly.id;

            // Check if setPaymentLinkLoading exists before calling
            if (setPaymentLinkLoading) setPaymentLinkLoading(true);

            // Close the modal immediately so user sees the payment loading/modal
            onClose();

            createPaymentLink(
                targetPlanId,
                currency,
                messageApi,
                setPaymentLinkLoading,
                setPaymentLinkData
            );
        }
    };

    return (
        <Modal
            open={open}
            footer={null}
            width={720}
            centered
            closable={false}
            className="onboarding-pricing-modal"
            maskClosable={false}
        >
            <div className="opm-content">
                {showFreeTrialSuccess ? (
                    <div className="opm-success-content" style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div style={{ fontSize: '60px', marginBottom: '20px' }}>🎉</div>
                        <h2 className="opm-title">Welcome to the Family!</h2>
                        <p className="opm-subtitle" style={{ maxWidth: '80%', margin: '0 auto 30px', fontSize: '16px', lineHeight: '1.6' }}>
                            Congratulations! You've unlocked <b>7 Days of Pro Access</b> for FREE without any additional cost.
                        </p>
                        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '20px', marginBottom: '30px', display: 'inline-block', textAlign: 'left', minWidth: '300px' }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#166534', fontSize: '16px' }}>What's Included:</h4>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                <li style={{ marginBottom: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><CheckCircleFilled style={{ color: '#166534' }} /> Unlimited Automations (No Limits)</li>
                                <li style={{ marginBottom: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><CheckCircleFilled style={{ color: '#166534' }} /> 1500 Replies / Month</li>
                                <li style={{ marginBottom: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><CheckCircleFilled style={{ color: '#166534' }} /> Full Flow Editor Access</li>
                                <li style={{ marginBottom: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><CheckCircleFilled style={{ color: '#166534' }} /> Priority Support</li>
                            </ul>
                        </div>
                        <br />
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => {
                                onClose();
                                window.location.href = '/home';
                            }}
                            style={{ minWidth: '200px', height: '50px', fontSize: '16px', borderRadius: '10px' }}
                        >
                            Go to Dashboard
                        </Button>
                    </div>
                ) : (
                    <>
                        <div className="opm-header">
                            <h2 className="opm-title">Choose a Plan to Continue 🚀</h2>
                            <p className="opm-subtitle">
                                Your automation is live! Pick a plan to continue growing.
                            </p>

                            <div className="opm-billing-toggle" style={{ marginTop: '20px' }}>
                                <span className={billingCycle === 'monthly' ? 'active' : ''}>Monthly</span>
                                <Switch
                                    checked={billingCycle === 'yearly'}
                                    onChange={(checked) => setBillingCycle(checked ? 'yearly' : 'monthly')}
                                    style={{ margin: '0 10px', backgroundColor: billingCycle === 'yearly' ? '#6366f1' : '#bfbfbf' }}
                                />
                                <span className={billingCycle === 'yearly' ? 'active' : ''}>
                                    Yearly <Tag color="green" style={{ marginLeft: '5px', borderRadius: '10px' }}>Save 20%</Tag>
                                </span>
                            </div>
                        </div>

                        <div className="opm-plans">
                            {plans.map((plan) => (
                                <div
                                    key={plan.id}
                                    className={`opm-plan-card ${plan.recommended ? 'recommended' : ''}`}
                                >
                                    {plan.recommended && (
                                        <div className="opm-popular-badge">Value for Money</div>
                                    )}

                                    <div className="opm-plan-icon">
                                        {typeof plan.icon === 'string' ? plan.icon : plan.icon}
                                    </div>

                                    <h3 className="opm-plan-name">{plan.name}</h3>

                                    <div className="opm-plan-price">
                                        <span className="opm-price">
                                            {billingCycle === 'yearly' ? plan.yearly.price : plan.monthly.price}
                                        </span>
                                        <span className="opm-period">/month <br />
                                            {billingCycle === 'yearly' &&
                                                <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#999' }}>(billed yearly)</span>
                                            }
                                        </span>
                                    </div>

                                    <ul className="opm-features">
                                        {plan.features.map((feature, idx) => (
                                            <li key={idx}>
                                                <CheckCircleFilled className="opm-check" />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>

                                    <Button
                                        type={plan.buttonType}
                                        size="large"
                                        className={`opm-plan-btn ${plan.recommended ? 'recommended' : ''}`}
                                        onClick={() => handleSelectPlan(plan)}
                                        block
                                    >
                                        {plan.buttonText}
                                    </Button>
                                </div>
                            ))}
                        </div>

                        {/* Separate Free Plan Section */}
                        <div className="opm-free-plan">
                            <div className="opm-free-header">
                                <span className="opm-free-icon">{freePlan.icon}</span>
                                <div>
                                    <h4 className="opm-free-title">{freePlan.name}</h4>
                                    <p className="opm-free-desc">1500 Automations / month</p>
                                </div>
                            </div>
                            <div className="opm-free-action">
                                <Button
                                    type={freePlan.buttonType}
                                    size="large"
                                    className="opm-free-btn"
                                    onClick={() => handleSelectPlan(freePlan)}
                                >
                                    {freePlan.buttonText}
                                </Button>
                            </div>
                        </div>

                        <p className="opm-footer-note">
                            💡 You can upgrade or change your plan anytime from Settings
                        </p>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default OnboardingPricingModal;
